import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Grid,
    Card,
    Divider,
    CardContent,
    IconButton,
    Avatar,
    Chip,
    Button,
    CircularProgress,
    Tabs,
    Tab,
    TextField,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import moment from "moment";
import { RxCross2 } from "react-icons/rx";
import StatusBadge from "components/badge/StatusBadge";
import CustomButton from "components/Button";
import { FaPlus } from "react-icons/fa";
import { useInitiateRFIMutation, useUpdateDesignEngMutation, useLazyGetAllDesignEngQuery } from "store/slices/apis/designEngineeringSlice"
import DocumentManagement from "components/DocumentManagement";
import { stageOptions, blockOptions_1, blockOptions_2, componentOptions, gfcStatusOptions } from "helpers/designEngineeringOptions";
import CalenderSVG from "assets/logo/Calendar";
import { useDispatch } from "store";
import { useParams } from "react-router-dom";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import DrawingTitleSearch from "./DrawingTitleSearch";
import WbsTreeView from "view/dashboard/wbsTreeView";
import { useGetMasterDataQuery } from "store/slices/apis/masterConfigation";
import { mapAndSortAlphabetically } from "utils/mapSortHelper";

const rightLabelStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
};

const rightTextStyle = {
    "& .MuiInputBase-input": {
        fontWeight: 400,
        fontSize: "16px",
        lineHeight: "100%",
        letterSpacing: "0.16px",
        color: "#0A0A0C",
        fontFamily: "Outfit, sans-serif",
    },
};

const labelTextStyle = {
    fontWeight: 500,
    fontSize: "16px",
    lineHeight: "100%",
    color: "#5F5F5F",
    fontFamily: "Outfit, sans-serif",
    mb: 1,
    mt: 1,
};

const EditDesignEng = (props) => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const [isEditMode, setIsEditMode] = useState(false);
    const [isIEEditMode, setIsIEEditMode] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const { open, handleClose, rowData, isLoading } = props;
    //const [isInitiating, setIsInitiating] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [initiateRfiRevision, { isLoading: isInitiating }] = useInitiateRFIMutation();

    const [updateDesignEng] = useUpdateDesignEngMutation()

    const [triggerSearch, { data: searchData, isFetching }] = useLazyGetAllDesignEngQuery();

    const module = "DesignAndEngineeringTracker";

    const { data: documentTypeOptionsRes } = useGetMasterDataQuery({ module: module, category: 'DocumentType', ProjectId: projectId });
    const documentTypeOptionsData = documentTypeOptionsRes?.data || [];
    const documentTypeOptions = mapAndSortAlphabetically(documentTypeOptionsData);

    const { data: consultantOptionsRes } = useGetMasterDataQuery({ module: module, category: 'Consultant', ProjectId: projectId });
    const consultantOptionsData = consultantOptionsRes?.data || [];
    const consultantOptions = (consultantOptionsData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const { data: disciplineOptionsRes } = useGetMasterDataQuery({ module: module, category: 'Discipline', ProjectId: projectId });
    const disciplineOptionsData = disciplineOptionsRes?.data || [];
    const disciplineOptions = mapAndSortAlphabetically(disciplineOptionsData);

    const { data: approvalStatusOptionsRes } = useGetMasterDataQuery({ module: module, category: 'IEApprovalStatus', ProjectId: projectId });
    const approvalStatusOptionsData = approvalStatusOptionsRes?.data || [];
    const approvalStatusOptions = (approvalStatusOptionsData || []).map(p => ({
        value: p?.value,
        label: p?.value,
    }));

    const submissionData = rowData?.submissions ? [...rowData.submissions] : [];
    const labelStyle = {
        fontSize: "14px",
        color: "#6b7280",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    };

    const valueStyle = {
        fontSize: "15px",
        color: "#111827",
        fontWeight: 500,
    };

    const getAvatarColor = (text) => {
        const colors = ["#1976D2", "#388E3C", "#F57C00", "#7B1FA2", "#0288D1", "#C2185B"];
        const index = text ? text.charCodeAt(0) % colors.length : 0;
        return colors[index];
    };

    const calculateDaysRemaining = () => {
        if (!rowData?.targetDate) return "N/A";

        const target = moment(rowData.targetDate);
        const today = moment();
        const days = target.diff(today, 'days');

        return days;
    };

    const daysRemaining = calculateDaysRemaining();
    const isOverdue = daysRemaining !== "N/A" && daysRemaining < 0;

    const handleInitiateRfiRevision = async () => {
        try {

            // unwrap() throws if the request fails => goes to catch
            const result = await initiateRfiRevision(rowData.designTrackerId).unwrap();

            // ✅ optional: if you want to refresh parent grid, call props.onSuccess?.(result)
            // props.onSuccess?.(result);

            // ✅ close dialog after success
            handleClose();
        } catch (err) {
            console.error("initiateRfiRevision failed:", err);

            // RTKQ error shapes vary (fetchBaseQuery gives {status, data})
            const msg =
                err?.data?.message ||
                err?.error ||
                "Failed to initiate RFI/Revision";

            alert(msg);
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const isGfcApprovedDate = Boolean(rowData?.gfcApprovalDate);
    const isPendingStatus = rowData?.status === "Planned" || rowData?.status === "Pending";
    const ifcProcess = !isGfcApprovedDate && isPendingStatus;

    const isRFIInProgress = isGfcApprovedDate && rowData?.status === "In Progress";

    const showIEEdit = isGfcApprovedDate && rowData?.status === "Completed";

    const getOldWbsData = () => ({
        projectCode: rowData?.projectCode ?? "",
        subDesign1: rowData?.subDesign1 ?? "",
        subDesign2: rowData?.subDesign2 ?? "",
        subDesign3: rowData?.subDesign3 ?? "",
        subDesign4: rowData?.subDesign4 ?? "",
        subDesign5: rowData?.subDesign5 ?? "",
    });

    const initialFormData = {
        //component: rowData?.component || "",
        //block: rowData?.block || "",
        designType: rowData?.designType || "",
        consultant: rowData?.consultant || "",
        discipline: rowData?.discipline || "",
        drgNo: rowData?.drgNo || "",
        title: rowData?.title || "",
        targetDate: moment.utc(rowData?.targetDate)?.format("YYYY-MM-DD") || "",
        ieApprovalStatus: rowData?.ieApprovalStatus || "",
        ieApprovalDate: moment.utc(rowData?.ieApprovalDate)?.format("YYYY-MM-DD") || "",
    };

    const normalValidationSchema = () => {
        let schema = {
            //component: Yup.string().required("Component field is required"),
            //block: Yup.string().required("Block field is required"),
            designType: Yup.string().required("Document Type is required"),
            consultant: Yup.string().required("Consultant field is required"),
            discipline: Yup.string().required("Discipline field is required"),
            drgNo: Yup.string().required("Drawing Number field is required"),
            title: Yup.string().required("Title field is required"),
            targetDate: Yup.date()
                .required("Target Date field is required")
                .typeError("Please enter a valid date"),

        };

        return Yup.object(schema);
    };

    const ieStatusValidationSchema = () => {
        let schema = {

            ieApprovalStatus: Yup.string(),

            ieApprovalDate: Yup.mixed().when("ieApprovalStatus", {
                is: (val) => val === "Approved",
                then: () =>
                    Yup.date()
                        .typeError("IE Approval Date is required")
                        .required("IE Approval Date is required"),

                otherwise: () =>
                    Yup.mixed().notRequired(),
            }),

        };

        return Yup.object(schema);
    };

    const handleEdit = () => {
        setIsEditMode(true);
        setShowSuggestions(false);
    }
    const handleCancel = (resetForm) => {
        resetForm();
        setIsEditMode(false);
        setIsIEEditMode(false);
        setShowSuggestions(false);
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const mapWbsIdsToSubcats = (wbsIdsPath) => {
        const ids = wbsIdsPath
            ? wbsIdsPath.split(",").map(id => id.trim())
            : [];

        return {
            subDesign1: ids[0] ?? "",
            subDesign2: ids[1] ?? "",
            subDesign3: ids[2] ?? "",
            subDesign4: ids[3] ?? "",
            subDesign5: ids[4] ?? "",
        };
    }

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        const formatDate = (dateString) => {
            if (!dateString || dateString === "" || dateString === "Invalid Date") {
                return null;
            }
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date.toISOString();
        };

        const wbsPayload = values.wbsNamePath
            ? {
                projectCode: values.wbsNamePath,
                ...mapWbsIdsToSubcats(values.wbsIdsPath),
            }
            : getOldWbsData();

        let formattedData = {};

        if (isIEEditMode) {
            formattedData = {
                projectId: projectId,
                designTrackerId: rowData?.designTrackerId,
                designType: rowData?.designType,

                ...wbsPayload,

                consultant: rowData?.consultant,
                discipline: rowData?.discipline,
                drgNo: rowData?.drgNo,
                title: rowData?.title,
                targetDate: rowData?.targetDate || null,
                status: rowData?.status,
                ieApprovalStatus: values.ieApprovalStatus,
                ieApprovalDate: values.ieApprovalStatus === "Approved" ? formatDate(values.ieApprovalDate) : null,
                isActive: true,
                gfcApprovalStatus: rowData?.gfcApprovalStatus,
                gfcApprovalDate: rowData?.gfcApprovalDate || null,
            };
        } else {
            formattedData = {
                //projectCode: "string",
                projectId: projectId,
                designTrackerId: rowData?.designTrackerId,
                //component: values.component,
                //block: values.block,
                designType: values.designType,

                ...wbsPayload,

                consultant: values.consultant,
                discipline: values.discipline,
                drgNo: values.drgNo,
                title: values.title,
                targetDate: formatDate(values.targetDate) || null,
                status: rowData?.status,
                isActive: true,
                gfcApprovalStatus: rowData?.gfcApprovalStatus,
                gfcApprovalDate: rowData?.gfcApprovalDate || null,
            };
        }

        try {
            const response = await updateDesignEng({ body: formattedData }).unwrap();
            openNotification("Drawing updated successfully!", "success");
            resetForm();
            setSubmitting(false);
            setIsEditMode(false);
            setIsIEEditMode(false);
            handleClose();
        } catch (error) {
            openNotification(error?.data?.message ?? "Something went wrong", "error");
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderTopLeftRadius: "5px",
                    borderTopRightRadius: "5px",
                    backgroundColor: "#FFFFFF",
                    width: "100%",
                    overflowX: "hidden"
                },
            }}
            disableEscapeKeyDown
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    handleClose();
                }
            }}
        >
            <DialogTitle sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ECECEC",
                pb: 2,
                pt: 3,
                width: "100%",
                position: "relative",
            }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {/* <Avatar
                        sx={{
                            width: 40,
                            height: 40,
                            bgcolor: getAvatarColor(rowData?.consultant || ""),
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: "bold",
                            textTransform: "uppercase",
                        }}
                    >
                        {rowData?.consultant?.[0] || "D"}
                    </Avatar> */}
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: "27px",
                        lineHeight: "100%",
                        color: "#140F36",
                        fontFamily: "Outfit, sans-serif"
                    }}>
                        Drawing Details
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {!isGfcApprovedDate && !isPendingStatus ? null : (
                        <CustomButton
                            label={
                                isRFIInProgress
                                    ? "RFI In Progress"
                                    :
                                    isGfcApprovedDate
                                        ? "Initiate RFI/Revision"
                                        : "Initiate IFC Process"
                            }
                            variant="contained"
                            color="primary"
                            disabled={isInitiating || !rowData?.designTrackerId}
                            onClick={handleInitiateRfiRevision}
                            sx={{
                                textTransform: "none",
                                borderRadius: "8px",
                                fontWeight: 600,
                                mr: 10, // because your close icon is absolute with right:50
                            }}
                        >
                            {isInitiating ? (
                                <CircularProgress size={18} />
                            ) : (
                                isRFIInProgress
                                    ? "RFI In Progress"
                                    :
                                    isGfcApprovedDate
                                        ? "Initiate RFI/Revision"
                                        : "Initiate IFC Process"
                            )}
                        </CustomButton>
                    )}
                    <IconButton
                        aria-label="close"
                        onClick={() => handleClose()}
                        sx={(theme) => ({
                            position: "absolute",
                            right: 50,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <RxCross2 style={{ fontSize: "30px", color: "#140F36" }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>

                <Formik
                    initialValues={initialFormData}
                    validationSchema={
                        isIEEditMode
                            ? ieStatusValidationSchema
                            : normalValidationSchema
                    }
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        setFieldTouched,
                        resetForm,
                    }) => {

                        const blockOptionsByComponent = {
                            [componentOptions?.[0]?.value]: blockOptions_1,
                            [componentOptions?.[1]?.value]: blockOptions_2,
                        };

                        const selectedBlockOptions =
                            blockOptionsByComponent[values.component] ?? [];

                        const hasSearchResults = Array.isArray(searchData?.data) && searchData?.data?.length > 0;

                        return (
                            <>
                                <DrawingTitleSearch
                                    component={values.component}
                                    block={values.block}
                                    consultant={values.consultant}
                                    discipline={values.discipline}
                                    projectId={projectId}
                                    onSearch={triggerSearch}
                                />

                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                borderBottom: "2px solid #ECECEC",
                                                overflowX: "auto",
                                                whiteSpace: "nowrap",
                                                p: 0,
                                                // display: "flex", alignItems: "center", justifyContent: 'space-between',
                                                "-webkit-overflow-scrolling": "touch",
                                            }}
                                        >
                                            <Tabs
                                                value={tabValue}
                                                onChange={handleTabChange}
                                                TabIndicatorProps={{ style: { display: "none" } }}
                                                variant="scrollable"
                                                scrollButtons={false}
                                                sx={{
                                                    justifyContent: "flex-start !important",
                                                    alignItems: "flex-start",
                                                    "& .MuiTabs-flexContainer": {
                                                        justifyContent: "flex-start !important",
                                                        gap: "30px",
                                                    },
                                                }}
                                            >
                                                {/* {["Drawing Information", "Documents"].map((label, index) => { */}
                                                {["Drawing Information"].map((label, index) => {
                                                    return (
                                                        <Tab
                                                            key={index}
                                                            label={
                                                                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                                    <Typography
                                                                        sx={{
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            color: tabValue === index ? "#0E73F6" : "#7E7E7E",
                                                                            fontFamily: "Outfit, sans-serif"
                                                                        }}
                                                                    >
                                                                        {label}
                                                                    </Typography>

                                                                </Box>
                                                            }
                                                            sx={{
                                                                textTransform: "none",
                                                                minWidth: "auto",
                                                                paddingBottom: "14px",
                                                                paddingLeft: 0,
                                                                paddingRight: 0,
                                                                borderBottom: tabValue === index ? "2px solid #0E73F6" : "2px solid transparent",
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Tabs>
                                        </Box>
                                    </Grid>
                                    {tabValue == 0 &&
                                        <form onSubmit={handleSubmit}>
                                            <Grid size={12}>
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        borderRadius: "3px",
                                                        border: "1px solid #e5e7eb",
                                                        backgroundColor: "#fff",
                                                        p: 2,
                                                    }}
                                                >

                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <Typography sx={{
                                                            fontWeight: 600,
                                                            fontSize: "20px",
                                                            fontFamily: "Outfit, sans-serif",
                                                            color: "#0A0A0C"
                                                        }}>
                                                            Drawing Information
                                                        </Typography>

                                                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                            {showIEEdit && !isEditMode && (
                                                                <>
                                                                    {!isIEEditMode ? (
                                                                        <Button
                                                                            variant="contained"
                                                                            onClick={() => setIsIEEditMode(true)}
                                                                            sx={{
                                                                                backgroundColor: "#00344C",
                                                                                borderRadius: "25px",
                                                                                textTransform: "none",
                                                                                fontWeight: 500,
                                                                                fontSize: "14px",
                                                                                lineHeight: "100%",
                                                                                fontFamily: "Outfit, sans-serif",
                                                                                color: "#FFFFFF",
                                                                                px: "20px",
                                                                                py: "15px",
                                                                            }}
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                    ) : (
                                                                        <>
                                                                            {/* Top IE Buttons */}
                                                                            <Typography
                                                                                onClick={() => handleCancel(resetForm)}
                                                                                sx={{
                                                                                    textTransform: "none",
                                                                                    color: "#F15744",
                                                                                    fontSize: "14px",
                                                                                    lineHeight: "100%",
                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                    borderRadius: "25px",
                                                                                    fontWeight: 500,
                                                                                    px: "20px",
                                                                                    py: "15px",
                                                                                    mr: 2,
                                                                                    cursor: "pointer",
                                                                                }}
                                                                            >
                                                                                Cancel
                                                                            </Typography>
                                                                            <Button
                                                                                type="submit"
                                                                                variant="contained"
                                                                                sx={{
                                                                                    backgroundColor: "#00344C",
                                                                                    borderRadius: "25px",
                                                                                    textTransform: "none",
                                                                                    fontWeight: 500,
                                                                                    fontSize: "14px",
                                                                                    lineHeight: "100%",
                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                    color: "#FFFFFF",
                                                                                    px: "20px",
                                                                                    py: "15px",
                                                                                }}
                                                                            >
                                                                                Update
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}

                                                            {ifcProcess && (
                                                                !isEditMode ? (
                                                                    <Button
                                                                        variant="contained"
                                                                        onClick={handleEdit}
                                                                        sx={{
                                                                            backgroundColor: "#00344C",
                                                                            borderRadius: "25px",
                                                                            textTransform: "none",
                                                                            fontWeight: 500,
                                                                            fontSize: "14px",
                                                                            lineHeight: "100%",
                                                                            fontFamily: "Outfit, sans-serif",
                                                                            color: "#FFFFFF",
                                                                            px: "20px",
                                                                            py: "15px",
                                                                            // "&:hover": {
                                                                            //     backgroundColor: "#002233",
                                                                            // },
                                                                        }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                ) : (
                                                                    <>
                                                                        <Typography
                                                                            onClick={() => handleCancel(resetForm)}
                                                                            sx={{
                                                                                textTransform: "none",
                                                                                color: "#F15744",
                                                                                fontSize: "14px",
                                                                                lineHeight: "100%",
                                                                                fontFamily: "Outfit, sans-serif",
                                                                                borderRadius: "25px",
                                                                                fontWeight: 500,
                                                                                px: "20px",
                                                                                py: "15px",
                                                                                mr: 2,
                                                                                cursor: "pointer",
                                                                            }}
                                                                        >
                                                                            Cancel
                                                                        </Typography>
                                                                        <Button
                                                                            type="submit"
                                                                            variant="contained"
                                                                            sx={{
                                                                                backgroundColor: "#00344C",
                                                                                borderRadius: "25px",
                                                                                textTransform: "none",
                                                                                fontWeight: 500,
                                                                                fontSize: "14px",
                                                                                lineHeight: "100%",
                                                                                fontFamily: "Outfit, sans-serif",
                                                                                color: "#FFFFFF",
                                                                                px: "20px",
                                                                                py: "15px",
                                                                                // "&:hover": {
                                                                                //     backgroundColor: "#002233",
                                                                                // },
                                                                            }}
                                                                        >
                                                                            Update
                                                                        </Button>
                                                                    </>
                                                                )
                                                            )}
                                                        </Box>
                                                    </Box>

                                                    <Divider sx={{ mb: 3 }} />

                                                    <CardContent sx={{ pt: 0 }}>
                                                        <Grid container spacing={2}>

                                                            <Grid size={{ xs: 12 }}>
                                                                <Grid container spacing={2}>

                                                                    {isIEEditMode && (
                                                                        <>

                                                                            <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2, mb: 6 }}>

                                                                                {/* IE Approval Status */}
                                                                                <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                                                    Select IE Approval Status
                                                                                </Typography>
                                                                                <Select
                                                                                    name="ieApprovalStatus"
                                                                                    options={approvalStatusOptions}
                                                                                    value={approvalStatusOptions.find(opt => opt.value === values.ieApprovalStatus) || null}
                                                                                    onChange={(option) => {
                                                                                        const value = option?.value || "";
                                                                                        setFieldValue("ieApprovalStatus", value);

                                                                                        if (value !== "Approved") {
                                                                                            setFieldValue("ieApprovalDate", "");
                                                                                        }
                                                                                    }
                                                                                    }
                                                                                    onBlur={() => setFieldTouched("ieApprovalStatus", true)}
                                                                                    isDisabled={rowData?.gfcApprovalStatus?.trim()?.toLowerCase() !== "received"}
                                                                                    placeholder="Select IE Approval Status"
                                                                                />
                                                                                {touched.ieApprovalStatus && errors.ieApprovalStatus && (
                                                                                    <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                        {errors.ieApprovalStatus}
                                                                                    </Typography>
                                                                                )}
                                                                            </Grid>

                                                                            {/* IE Approval Date */}
                                                                            {values.ieApprovalStatus === "Approved" && (
                                                                                <Grid size={{ xs: 12, md: 6 }} sx={{ mt: 2, mb: 6 }}>
                                                                                    <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                                                        IE Approval Date
                                                                                    </Typography>
                                                                                    <TextField
                                                                                        type="date"
                                                                                        name="ieApprovalDate"
                                                                                        value={values.ieApprovalDate || ""}
                                                                                        onChange={handleChange}
                                                                                        onBlur={handleBlur}
                                                                                        onClick={(e) => {
                                                                                            const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                                            if (input) input.showPicker?.();
                                                                                        }}
                                                                                        fullWidth
                                                                                        sx={{
                                                                                            ...rightTextStyle,
                                                                                            "& input::-webkit-calendar-picker-indicator": {
                                                                                                display: "none",
                                                                                                WebkitAppearance: "none",
                                                                                            },
                                                                                            "& input[type='date']": {
                                                                                                appearance: "none",
                                                                                                WebkitAppearance: "none",
                                                                                            },
                                                                                            "& .MuiOutlinedInput-root": {
                                                                                                "& fieldset": {
                                                                                                    border: "none",
                                                                                                },
                                                                                            },
                                                                                        }}
                                                                                        InputProps={{
                                                                                            startAdornment: (
                                                                                                <Box sx={{
                                                                                                    ml: 2,
                                                                                                    mr: 1,
                                                                                                    color: "#433F5E",
                                                                                                    display: "flex",
                                                                                                    alignItems: "center",
                                                                                                    cursor: "pointer"
                                                                                                }}>
                                                                                                    {CalenderSVG}
                                                                                                </Box>
                                                                                            ),
                                                                                            sx: {
                                                                                                backgroundColor: "#fff",
                                                                                                border: '1px solid',
                                                                                                borderColor: 'hsl(0, 0%, 80%)',
                                                                                                height: "40px",
                                                                                                width: { xs: "100%", md: "100%" },
                                                                                                borderRadius: "5px",
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                    {errors.ieApprovalDate && (
                                                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                            {errors.ieApprovalDate}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Grid>
                                                                            )}

                                                                            <Grid size={12}>
                                                                                <Divider sx={{ mt: 2, mb: 3 }} />
                                                                            </Grid>
                                                                        </>
                                                                    )}

                                                                    {!isEditMode ? (
                                                                        <>
                                                                            <Grid size={{ xs: 12 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Asset:</strong>
                                                                                </Typography>
                                                                                <Typography sx={valueStyle}>
                                                                                    {rowData?.projectCode ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid>
                                                                            {/* <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Component</strong>
                                                                                </Typography>
                                                                                <Typography sx={valueStyle}>
                                                                                    {rowData?.component ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Block:</strong>
                                                                                </Typography>
                                                                                <Typography sx={valueStyle}>
                                                                                    {rowData?.block ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid> */}
                                                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Consultant:</strong>
                                                                                </Typography>
                                                                                <Box sx={{ display: "flex", alignItems: 'center', gap: 1, mb: 2, p: 0 }}>
                                                                                    <Typography sx={valueStyle}>
                                                                                        {rowData?.consultant ?? "N/A"}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>

                                                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Discipline:</strong>
                                                                                </Typography>
                                                                                <Typography sx={valueStyle}>
                                                                                    {rowData?.discipline ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid>

                                                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Drawing Number:</strong>
                                                                                </Typography>
                                                                                <Typography sx={valueStyle}>
                                                                                    {rowData?.drgNo ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid>

                                                                            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Drawing Type:</strong>
                                                                                </Typography>
                                                                                <Typography sx={valueStyle}>
                                                                                    {rowData?.designType ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid>

                                                                            <Grid size={{ xs: 12 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Drawing Title:</strong>
                                                                                </Typography>
                                                                                <Typography sx={{
                                                                                    fontSize: "18px",
                                                                                    color: "#111827",
                                                                                    fontWeight: 600,
                                                                                    mb: 2,
                                                                                    fontFamily: "Outfit, sans-serif",
                                                                                    padding: "8px",
                                                                                    backgroundColor: "#f8f9fa",
                                                                                    borderRadius: "4px",
                                                                                    borderLeft: "4px solid #0E73F6"
                                                                                }}>
                                                                                    {rowData?.title ?? "N/A"}
                                                                                </Typography>
                                                                            </Grid>

                                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Target Date (Drawing Receive):</strong>
                                                                                </Typography>
                                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                                                                    <Typography sx={{
                                                                                        ...valueStyle,
                                                                                        color: "#111827",
                                                                                        fontWeight: 600
                                                                                    }}>
                                                                                        {rowData?.targetDate ?
                                                                                            moment(rowData.targetDate).format("DD MMM YYYY") :
                                                                                            "N/A"}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid>

                                                                            {/* <Grid size={{ xs: 12, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>IE Approval Status:</strong>
                                                                                </Typography>
                                                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                                                                    <Typography sx={{
                                                                                        ...valueStyle,
                                                                                        color: "#111827",
                                                                                        fontWeight: 600
                                                                                    }}>
                                                                                        {rowData?.ieApprovalStatus ?? "N/A"}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid> */}

                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                                <Grid container spacing={1}>
                                                                                    <Grid size={{ xs: 12 }}>
                                                                                        <Typography sx={labelStyle}>
                                                                                            <strong>Asset:</strong>
                                                                                        </Typography>
                                                                                        <Typography sx={valueStyle}>
                                                                                            {rowData?.projectCode ?? "N/A"}
                                                                                        </Typography>
                                                                                    </Grid>

                                                                                    <Grid size={12}>
                                                                                        <Typography sx={{
                                                                                            mt: 1,
                                                                                            mb: 1,
                                                                                            fontSize: "16px",
                                                                                            fontWeight: 500,
                                                                                            fontFamily: "Outfit, sans-serif",
                                                                                            color: "#5F5F5F"
                                                                                        }}>
                                                                                            Asset Selector
                                                                                        </Typography>

                                                                                        <WbsTreeView
                                                                                            projectId={projectId}
                                                                                            parentId="683"
                                                                                            onSelect={function (node) {
                                                                                                // node = { id, name, raw, isLeaf }

                                                                                                // Save into form values
                                                                                                setFieldValue("wbsId", node.id);
                                                                                                setFieldValue("wbsNamePath", node.fullPath);
                                                                                                setFieldValue("wbsIdsPath", node.idPath);

                                                                                                setFieldTouched("wbsNamePath", true);
                                                                                            }}
                                                                                        />

                                                                                        {/* Optional: show selected WBS name inside popup */}
                                                                                        {values.wbsNamePath && (
                                                                                            <Typography sx={{ mt: 2, fontSize: 14, color: "#555" }}>
                                                                                                Selected Asset: <strong>{values.wbsNamePath}</strong>
                                                                                            </Typography>
                                                                                        )}

                                                                                        {/* {touched.wbsNamePath && errors.wbsNamePath && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.wbsNamePath}
                                                                                            </Typography>
                                                                                        )} */}
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </Grid>

                                                                            <Grid size={{ xs: 12, md: 6 }}>
                                                                                <Grid container spacing={2}>
                                                                                    <Grid size={12}>
                                                                                        <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                                                            Select Document Type
                                                                                        </Typography>
                                                                                        <Select
                                                                                            name="designType"
                                                                                            options={documentTypeOptions}
                                                                                            value={documentTypeOptions.find(opt => opt.value === values.designType) || null}
                                                                                            onChange={(option) => {
                                                                                                const comp = option?.value ?? "";
                                                                                                setFieldValue("designType", comp);
                                                                                            }}
                                                                                            onBlur={() => setFieldTouched("designType", true)}
                                                                                            placeholder="Select Document Type"
                                                                                        />
                                                                                        {touched.designType && errors.designType && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.designType}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>

                                                                                    {/* <Grid size={{ xs: 12, md: 6 }}>
                                                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                                                        Select Component
                                                                                    </Typography>
                                                                                    <Select
                                                                                        name="component"
                                                                                        options={componentOptions}
                                                                                        value={componentOptions.find(opt => opt.value === values.component) || null}
                                                                                        onChange={(option) => {
                                                                                            const comp = option?.value ?? "";
                                                                                            setFieldValue("component", comp);

                                                                                            // reset dependent field
                                                                                            setFieldValue("block", "");
                                                                                            setFieldTouched("block", false, false);
                                                                                            setShowSuggestions(true);
                                                                                        }}
                                                                                        onBlur={() => setFieldTouched("component", true)}
                                                                                        placeholder="Select Component"
                                                                                    />
                                                                                    {touched.component && errors.component && (
                                                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                            {errors.component}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Grid>
                                                                                <Grid size={{ xs: 12, md: 6 }}>
                                                                                    <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                                                        Select Block
                                                                                    </Typography>
                                                                                    <Select
                                                                                        name="block"
                                                                                        options={selectedBlockOptions}
                                                                                        value={selectedBlockOptions.find(opt => opt.value === values.block) || null}
                                                                                        //value={blockOptions.find(opt => opt.value === values.block) || null}
                                                                                        onChange={(option) => {
                                                                                            setFieldValue("block", option.value);
                                                                                            setShowSuggestions(true);
                                                                                        }}
                                                                                        onBlur={() => setFieldTouched("block", true)}
                                                                                        placeholder="Select Block"
                                                                                    />
                                                                                    {touched.block && errors.block && (
                                                                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                            {errors.block}
                                                                                        </Typography>
                                                                                    )}
                                                                                </Grid> */}

                                                                                    <Grid size={12}>
                                                                                        <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                                                            Select Consultant
                                                                                        </Typography>
                                                                                        <Select
                                                                                            name="consultant"
                                                                                            options={consultantOptions}
                                                                                            value={consultantOptions.find(opt => opt.value === values.consultant) || null}
                                                                                            onChange={(option) => {
                                                                                                setFieldValue("consultant", option.value);
                                                                                                setShowSuggestions(true);
                                                                                            }}
                                                                                            onBlur={() => setFieldTouched("consultant", true)}
                                                                                            placeholder="Select Consultant"
                                                                                        />
                                                                                        {touched.consultant && errors.consultant && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.consultant}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>

                                                                                    <Grid size={12}>
                                                                                        <Typography sx={{ ...rightLabelStyle, mt: 1, mb: 1 }}>
                                                                                            Select Discipline
                                                                                        </Typography>
                                                                                        <Select
                                                                                            name="discipline"
                                                                                            options={disciplineOptions}
                                                                                            value={disciplineOptions.find(opt => opt.value === values.discipline) || null}
                                                                                            onChange={(option) => {
                                                                                                setFieldValue("discipline", option.value);
                                                                                                setShowSuggestions(true);
                                                                                            }}
                                                                                            onBlur={() => setFieldTouched("discipline", true)}
                                                                                            placeholder="Select Discipline"
                                                                                        />
                                                                                        {touched.discipline && errors.discipline && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.discipline}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>

                                                                                    {showSuggestions && values.component &&
                                                                                        values.block &&
                                                                                        values.consultant &&
                                                                                        values.discipline && hasSearchResults && (
                                                                                            <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 2, mb: 1, p: 2, border: "2px solid #ECECEC", borderRadius: 2 }}>
                                                                                                <Typography
                                                                                                    sx={{
                                                                                                        fontWeight: 600,
                                                                                                        fontSize: "14px",
                                                                                                        mb: 2,
                                                                                                        color: "#5F5F5F",
                                                                                                    }}
                                                                                                >
                                                                                                    We found the below drawings already part of DCI. Please ensure that you do not add duplicate drawing to DCI.
                                                                                                </Typography>
                                                                                                {isFetching ? (
                                                                                                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                                                                                        <CircularProgress size={24} />
                                                                                                    </Box>
                                                                                                ) : (
                                                                                                    <TableContainer component={Paper}
                                                                                                        sx={{
                                                                                                            maxHeight: searchData?.data?.length > 3 ? 180 : "auto",
                                                                                                            overflowY: searchData?.data?.length > 3 ? "auto" : "visible",
                                                                                                            //boxShadow: "none",
                                                                                                            //borderBottom: "none",
                                                                                                        }}>
                                                                                                        <Table size="medium">
                                                                                                            <TableHead>
                                                                                                                <TableRow sx={{ backgroundColor: "#F5F5F5" }}>
                                                                                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                                                                                        Drawing Number
                                                                                                                    </TableCell>
                                                                                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                                                                                        Drawing Title
                                                                                                                    </TableCell>
                                                                                                                </TableRow>
                                                                                                            </TableHead>

                                                                                                            <TableBody>

                                                                                                                {searchData?.data.map((row, index) => (
                                                                                                                    <TableRow key={index}>
                                                                                                                        <TableCell sx={{ fontSize: "13px" }}>{row.drgNo}</TableCell>
                                                                                                                        <TableCell sx={{ fontSize: "13px" }}>{row.title}</TableCell>
                                                                                                                    </TableRow>
                                                                                                                ))
                                                                                                                }
                                                                                                            </TableBody>
                                                                                                        </Table>
                                                                                                    </TableContainer>
                                                                                                )}
                                                                                            </Grid>
                                                                                        )}

                                                                                    <Grid size={12}>
                                                                                        <Typography sx={labelTextStyle}>
                                                                                            Drawing Title
                                                                                        </Typography>
                                                                                        <TextField
                                                                                            fullWidth
                                                                                            name="title"
                                                                                            value={values.title}
                                                                                            onChange={handleChange}
                                                                                            onBlur={handleBlur}
                                                                                            placeholder="Enter Drawing Title"
                                                                                            InputProps={{
                                                                                                sx: {
                                                                                                    height: "40px",
                                                                                                    borderRadius: "8px",
                                                                                                    backgroundColor: "#fff",
                                                                                                    "& input::placeholder": {
                                                                                                        fontWeight: 400,
                                                                                                        fontSize: "16px",
                                                                                                        lineHeight: "100%",
                                                                                                        letterSpacing: "0.16px",
                                                                                                        color: "#0A0A0C",
                                                                                                        fontFamily: "Outfit, sans-serif",
                                                                                                        opacity: 1,
                                                                                                    },
                                                                                                    "& input": {
                                                                                                        fontWeight: 400,
                                                                                                        fontSize: "16px",
                                                                                                        lineHeight: "100%",
                                                                                                        letterSpacing: "0.16px",
                                                                                                        color: "#0A0A0C",
                                                                                                        fontFamily: "Outfit, sans-serif",
                                                                                                        "&:-webkit-autofill": {
                                                                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                                                            WebkitTextFillColor: "#0A0A0C",
                                                                                                        },
                                                                                                        "&:-webkit-autofill:hover": {
                                                                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                                                            WebkitTextFillColor: "#0A0A0C",
                                                                                                        },
                                                                                                        "&:-webkit-autofill:focus": {
                                                                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                                                            WebkitTextFillColor: "#0A0A0C",
                                                                                                        },
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                        {touched.title && errors.title && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.title}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>

                                                                                    <Grid size={12}>
                                                                                        <Typography sx={labelTextStyle}>
                                                                                            Drawing Number
                                                                                        </Typography>
                                                                                        <TextField
                                                                                            fullWidth
                                                                                            name="drgNo"
                                                                                            value={values.drgNo}
                                                                                            onChange={handleChange}
                                                                                            onBlur={handleBlur}
                                                                                            placeholder="Enter Drawing Number"
                                                                                            InputProps={{
                                                                                                sx: {
                                                                                                    height: "40px",
                                                                                                    borderRadius: "8px",
                                                                                                    backgroundColor: "#fff",
                                                                                                    "& input::placeholder": {
                                                                                                        fontWeight: 400,
                                                                                                        fontSize: "16px",
                                                                                                        lineHeight: "100%",
                                                                                                        letterSpacing: "0.16px",
                                                                                                        color: "#0A0A0C",
                                                                                                        fontFamily: "Outfit, sans-serif",
                                                                                                        opacity: 1,
                                                                                                    },
                                                                                                    "& input": {
                                                                                                        fontWeight: 400,
                                                                                                        fontSize: "16px",
                                                                                                        lineHeight: "100%",
                                                                                                        letterSpacing: "0.16px",
                                                                                                        color: "#0A0A0C",
                                                                                                        fontFamily: "Outfit, sans-serif",
                                                                                                        "&:-webkit-autofill": {
                                                                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                                                            WebkitTextFillColor: "#0A0A0C",
                                                                                                        },
                                                                                                        "&:-webkit-autofill:hover": {
                                                                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                                                            WebkitTextFillColor: "#0A0A0C",
                                                                                                        },
                                                                                                        "&:-webkit-autofill:focus": {
                                                                                                            WebkitBoxShadow: "0 0 0 1000px white inset",
                                                                                                            WebkitTextFillColor: "#0A0A0C",
                                                                                                        },
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                        {touched.drgNo && errors.drgNo && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.drgNo}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>

                                                                                    <Grid size={12}>
                                                                                        <Typography sx={{ ...rightLabelStyle, mb: 1, mt: 1 }}>
                                                                                            Target Date
                                                                                        </Typography>
                                                                                        <TextField
                                                                                            type="date"
                                                                                            name="targetDate"
                                                                                            value={values.targetDate}
                                                                                            onChange={handleChange}
                                                                                            onBlur={handleBlur}
                                                                                            onClick={(e) => {
                                                                                                const input = e.currentTarget.parentElement?.querySelector('input[type="date"]');
                                                                                                if (input) input.showPicker?.();
                                                                                            }}
                                                                                            fullWidth
                                                                                            sx={{
                                                                                                ...rightTextStyle,
                                                                                                "& input::-webkit-calendar-picker-indicator": {
                                                                                                    display: "none",
                                                                                                    WebkitAppearance: "none",
                                                                                                },
                                                                                                "& input[type='date']": {
                                                                                                    appearance: "none",
                                                                                                    WebkitAppearance: "none",
                                                                                                },
                                                                                                "& .MuiOutlinedInput-root": {
                                                                                                    "& fieldset": {
                                                                                                        border: "none",
                                                                                                    },
                                                                                                },
                                                                                            }}
                                                                                            InputProps={{
                                                                                                startAdornment: (
                                                                                                    <Box sx={{
                                                                                                        ml: 2,
                                                                                                        mr: 1,
                                                                                                        color: "#433F5E",
                                                                                                        display: "flex",
                                                                                                        alignItems: "center",
                                                                                                        cursor: "pointer"
                                                                                                    }}>
                                                                                                        {CalenderSVG}
                                                                                                    </Box>
                                                                                                ),
                                                                                                sx: {
                                                                                                    backgroundColor: "#fff",
                                                                                                    border: '1px solid',
                                                                                                    borderColor: 'hsl(0, 0%, 80%)',
                                                                                                    height: "40px",
                                                                                                    width: { xs: "100%", md: "100%" },
                                                                                                    borderRadius: "5px",
                                                                                                },
                                                                                            }}
                                                                                        />
                                                                                        {touched.targetDate && errors.targetDate && (
                                                                                            <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                                                                                {errors.targetDate}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Grid>

                                                                                </Grid>
                                                                            </Grid>
                                                                        </>
                                                                    )}

                                                                    <Grid size={{ xs: 12 }}>
                                                                        <Typography sx={{
                                                                            ...labelStyle,
                                                                            fontSize: "16px",
                                                                            fontWeight: 600,
                                                                            color: "#374151",
                                                                            mt: 2,
                                                                            mb: 1
                                                                        }}>
                                                                            <strong>Review Section:</strong>
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <Typography sx={labelStyle}>
                                                                            <strong>Latest Revision Date:</strong>
                                                                        </Typography>
                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                                                            <Typography sx={{
                                                                                ...valueStyle,
                                                                                // color: isOverdue ? "#d32f2f" : "#388e3c",
                                                                                // fontWeight: 600
                                                                            }}>
                                                                                {rowData?.lastSubmissionDate ?
                                                                                    moment(rowData.lastSubmissionDate).format("DD MMM YYYY") :
                                                                                    "N/A"}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                        <Typography sx={labelStyle}>
                                                                            <strong>Latest Revision Number:</strong>
                                                                        </Typography>
                                                                        <Typography sx={valueStyle}>
                                                                            {rowData?.lastRevisionNo ?? "N/A"}
                                                                        </Typography>
                                                                    </Grid>
                                                                    {submissionData.length > 0 ? (
                                                                        submissionData.map((item, index) => (
                                                                            <Grid
                                                                                key={item.designTrackerSubmissionId || index}
                                                                                size={{ xs: 12, sm: 6, md: 6 }}>
                                                                                <Typography sx={labelStyle}>
                                                                                    <strong>Review No:{index + 1}</strong>
                                                                                </Typography>
                                                                                <Box sx={{
                                                                                    display: "flex", flexDirection: "column",
                                                                                    justifyContent: "flex-start", gap: 0, mb: 2
                                                                                }}>
                                                                                    <Typography sx={valueStyle}>
                                                                                        {item?.submissionDate ? `Revision Date: ${moment(item.submissionDate).format("DD MMM YYYY")}` : "N/A"}
                                                                                    </Typography>
                                                                                    <Typography sx={{
                                                                                        fontSize: "15px",
                                                                                        color: "#111827",
                                                                                        fontWeight: 500,
                                                                                    }}>
                                                                                        {item && `Revision Number : ${item.revisionNo}`}
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Grid >
                                                                        ))
                                                                    ) : (
                                                                        <Typography sx={{
                                                                            fontSize: "15px",
                                                                            color: "#111827",
                                                                            fontWeight: 500,
                                                                        }}>
                                                                            No submissions available
                                                                        </Typography>
                                                                    )}

                                                                    <Grid size={{ xs: 12 }}>
                                                                        <Typography sx={{
                                                                            ...labelStyle,
                                                                            fontSize: "16px",
                                                                            fontWeight: 600,
                                                                            color: "#374151",
                                                                            mt: 2,
                                                                            mb: 1
                                                                        }}>
                                                                            <strong>Approval Section:</strong>
                                                                        </Typography>
                                                                        <Divider sx={{ mb: 2 }} />
                                                                    </Grid>

                                                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                        <Typography sx={labelStyle}>
                                                                            <strong>Status:</strong>
                                                                        </Typography>
                                                                        <Box sx={{ mb: 2 }}>
                                                                            <StatusBadge status={rowData?.status} />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <Typography sx={labelStyle}>
                                                                            <strong>GFC Approval Date:</strong>
                                                                        </Typography>
                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                                                                            <Typography sx={{
                                                                                ...valueStyle,
                                                                                color: "#111827",
                                                                                fontWeight: 600
                                                                            }}>
                                                                                {rowData?.gfcApprovalDate ?
                                                                                    moment(rowData.gfcApprovalDate).format("DD MMM YYYY") :
                                                                                    "N/A"}
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                                                        <Typography sx={labelStyle}>
                                                                            <strong>GFC Approval Status:</strong>
                                                                        </Typography>
                                                                        <Box sx={{ mb: 2 }}>
                                                                            <StatusBadge status={rowData?.gfcApprovalStatus} />
                                                                        </Box>
                                                                    </Grid>


                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </form>
                                    }
                                    {/* {tabValue == 1 &&
                                        <Grid size={12}>
                                            <DocumentManagement id={rowData?.designTrackerId} entityType="Engineering" customId={rowData?.customId} />
                                        </Grid>
                                    } */}
                                </Grid >
                            </>
                        )
                    }}
                </Formik >
            </DialogContent>
        </Dialog >
    );
};

export default EditDesignEng;